import type { NotifiableError } from "@bugsnag/js";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import {
  addBugsnagBreadcrumb,
  notifyBugsnag,
} from "@/react/helpers/bugsnagHelpers";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";
import type { Notification } from "@circle-react/types/Notification";

interface SendNotificationParams {
  body?: string;
  metadata?: Notification | Record<string, unknown>;
  title?: string;
  type?: "message" | "notification";
}

const mountNotificationBody = ({
  notification,
}: {
  notification: Notification;
}) => {
  try {
    return notification.notification_text_structure
      .map(entry => (notification[entry as keyof Notification] as string) || "")
      .join(" ");
  } catch {
    return "";
  }
};

export const useElectronNotification = () => {
  const { currentCommunity } = usePunditUserContext();
  const { electronAPI } = useElectronAPI();

  const icon =
    currentCommunity?.icon_url ||
    "https://assets-v3.circle.so/qv2902nizkth5o752l2cugc6y09e";

  const sendNotificationToDesktopApp = ({
    title,
    body,
    metadata,
    type,
  }: SendNotificationParams) => {
    if (electronAPI?.sendNotification) {
      try {
        // in case the notification has no body and no title, it means it was trigger from the opening of the modal
        if (type === "notification" && !metadata) {
          return;
        }

        electronAPI.sendNotification({
          title:
            title ||
            t("desktop_app.notifications.new_notification_generic_title"),
          body:
            body ||
            (metadata && "notification_text_structure" in metadata
              ? mountNotificationBody({
                  notification: metadata as Notification,
                })
              : ""),
          icon,
          metadata,
          currentCommunityData: {
            id: currentCommunity?.id,
            name: currentCommunity?.name,
            slug: currentCommunity?.slug,
            root_url: currentCommunity?.root_url,
            full_url: currentCommunity?.full_url,
          } as Partial<CurrentCommunity>,
          type,
        });
      } catch (error) {
        addBugsnagBreadcrumb("Failed to send notification to desktop app");
        notifyBugsnag(error as NotifiableError);
        console.warn("Failed to send notification to desktop app:", error);
      }
    }
  };

  const setDesktopAppNotificationCount = (count: number) => {
    if (electronAPI?.setNotificationCount) {
      try {
        // Ensure we only pass primitive values to avoid clone errors
        const communityId = currentCommunity?.id
          ? Number(currentCommunity.id)
          : undefined;
        electronAPI.setNotificationCount(count, communityId);
      } catch (error) {
        addBugsnagBreadcrumb("Failed to set desktop app notification count");
        notifyBugsnag(error as NotifiableError);
        console.warn("Failed to set desktop app notification count:", error);
      }
    }
  };

  const setDesktopAppUnreadMessageCount = (count: number) => {
    if (electronAPI?.setUnreadMessageCount) {
      try {
        // Ensure we only pass primitive values to avoid clone errors
        const communityId = currentCommunity?.id
          ? Number(currentCommunity.id)
          : undefined;
        electronAPI.setUnreadMessageCount(count, communityId);
      } catch (error) {
        addBugsnagBreadcrumb("Failed to set desktop app unread message count");
        notifyBugsnag(error as NotifiableError);
        console.warn("Failed to set desktop app unread message count:", error);
      }
    }
  };

  return {
    sendNotificationToDesktopApp,
    setDesktopAppNotificationCount,
    setDesktopAppUnreadMessageCount,
  };
};
